import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
    WeezHeader,
    Button,
    Card,
    Form,
    Input,
    SelectLabel,
    Message,
    OptionalForm,
    CheckboxLabel,
    RadioLabel,
    CurrencyInputLabel,
    ToggleContainer,
    Section,
    ToastContext,
    DateTimeInput,
    Loader
} from '@weezevent/nacre';
import { useForm, requiredField } from '../../utils/validators';
import ServiceModel from '../../models/serviceModel';
import TaxModel from '../../models/taxModel';
import ServiceConfigModel from '../../models/serviceConfig';
import { useHistory } from 'react-router-dom';
import EditorComponent from '../../nacre/components/editorComponent';
import Style from './serviceForm.module.css';
import { AdminStatus } from '../widgets/components/pages/new-widget';
import Auth from '../../utils/auth';
import SelectTaxes from '../../components/tax/selectTaxes';
import TicketForm from './services/ticketForm';
import moment from 'moment-timezone';
import useAppContext from '../../context/useAppContext';
import i18n from 'i18next';

function DateSettings({ formState, onChangeForm }) {
    const { t, i18n } = useTranslation();
    const locale = i18n.language;
    const currentYear = new Date().getFullYear();
    const [{ organization }] = useAppContext();

    const getClosestDate = React.useMemo(() => {
        const STEP = 5;
        let _date = moment.utc();
        let minutes = _date.minutes();
        minutes = Math.round(minutes / STEP) * STEP;
        return _date
            .add(1, 'hours')
            .set('minute', minutes)
            .set('second', 0)
            .format();
    }, []);

    const onChange = React.useCallback(
        data => {
            let conf = { ...formState.config };
            if (data.value === 'fixed') {
                conf.price_offset = { type: data.value, date: getClosestDate };
            } else {
                conf.price_offset = { type: data.value, to: 'order__sold_date', offset: { days: 0, hours: 0, minutes: 0 } };
            }
            onChangeForm('config', conf);
        },
        [onChangeForm, formState, getClosestDate]
    );

    if (!formState.config.price_offset) {
        return null;
    }

    return (
        <div>
            <SelectLabel
                value={formState.config.price_offset.type}
                label={t('ticket.services.form.content.advanced.typeDate')}
                options={[
                    {
                        key: 'default',
                        value: 'default',
                        text: t('ticket.services.form.content.advanced.defaultStartType')
                    },
                    {
                        key: 'fixed',
                        value: 'fixed',
                        text: t('ticket.services.form.content.advanced.fixedType')
                    },
                    {
                        key: 'offset',
                        value: 'offset',
                        text: t('ticket.services.form.content.advanced.offsetType')
                    }
                ]}
                onChange={(synth, data) => onChange(data)}
            />
            {formState.config.price_offset.type === 'fixed' && (
                <Section>
                    <DateTimeInput
                        className={Style['service_form__datetime']}
                        timeLabel={t('ticket.services.form.content.advanced.hoursFreeService')}
                        dateLabel={t('ticket.services.form.content.advanced.dateFreeService')}
                        onChange={(date, dateObject) => {
                            let conf = { ...formState.config };
                            conf.price_offset.date = date;
                            onChangeForm('config', conf);
                        }}
                        value={formState.config.price_offset.date}
                        dateProps={{ minYear: currentYear - 7, maxYear: currentYear + 15 }}
                        locale={locale}
                        timezone={organization.timezone}
                    />
                </Section>
            )}
            {formState.config.price_offset.type === 'offset' && (
                <Section>
                    <Form.Label>{t('ticket.services.form.content.advanced.offsetFreeService')}</Form.Label>
                    <div className={Style['counter-container']}>
                        <div className={Style['counter']}>
                            <Form.CounterLabel
                                label={t('ticket.services.form.content.advanced.offsetDay')}
                                placeholder={0}
                                style={{ width: '122px' }}
                                onChange={(e, value) => {
                                    let config = { ...formState.config };
                                    config.price_offset.offset.days = value;
                                    onChangeForm('config', config);
                                }}
                                value={formState.config.price_offset.offset?.days || 0}
                                min={0}
                                step={1}
                            />
                        </div>
                        <div className={Style['counter']}>
                            <Form.CounterLabel
                                label={t('ticket.services.form.content.advanced.offsetHours')}
                                placeholder={0}
                                style={{ width: '122px' }}
                                onChange={(e, value) => {
                                    let config = { ...formState.config };
                                    config.price_offset.offset.hours = value;
                                    onChangeForm('config', config);
                                }}
                                value={formState.config.price_offset.offset?.hours || 0}
                                min={0}
                                step={1}
                            />
                        </div>
                        <div className={Style['counter']}>
                            <Form.CounterLabel
                                label={t('ticket.services.form.content.advanced.offsetMinutes')}
                                placeholder={0}
                                style={{ width: '122px' }}
                                onChange={(e, value) => {
                                    let config = { ...formState.config };
                                    config.price_offset.offset.minutes = value;
                                    onChangeForm('config', config);
                                }}
                                value={formState.config.price_offset.offset?.minutes || 0}
                                min={0}
                                step={1}
                            />
                        </div>
                    </div>
                </Section>
            )}
        </div>
    );
}

const MAPPING_SERVICE = {
    update_ticket_form: TicketForm
};

function SpecificFormService({ formState, onChangeForm, services }) {
    if (!formState.service) {
        return null;
    }

    const Component = React.useMemo(() => {
        if (!formState.service || !services) {
            return null;
        }

        let service = services.find(s => s.uuid === formState.service);
        if (!service) {
            return null;
        }

        return MAPPING_SERVICE[service.service_type];
    }, [formState, services]);

    if (!Component) {
        return null;
    }

    return <Component formState={formState} onChangeForm={onChangeForm} />;
}

function ServiceAdvancedCard({ formState, onChangeForm, services }) {
    const { t } = useTranslation();

    return (
        <Card.FormStep
            title={t(`ticket.services.form.header.advancedTitleCard`)}
            subtitle={t(`ticket.services.form.header.advancedSubtitleCard`)}
            rightComponent={
                <Message type="tips" title={t('ticket.common.tips')} mini>
                    <Trans i18nKey="ticket.services.infos.tips-period" />
                </Message>
            }
        >
            <Form>
                <ToggleContainer
                    className={Style['toggle-container']}
                    helpLabel={t('ticket.services.form.content.advanced.toggleSetDateSublabel')}
                    checked={formState.config.price_offset}
                    title={t('ticket.services.form.content.advanced.toggleSetDate')}
                    onChange={(synth, { checked }) => {
                        if (!checked) {
                            let conf = { ...formState.config };
                            if (conf.price_offset) {
                                delete conf.price_offset;
                            }
                            onChangeForm('config', conf);
                        } else {
                            let conf = { ...formState.config, price_offset: { type: 'default' } };
                            onChangeForm('config', conf);
                        }
                    }}
                />
                <DateSettings formState={formState} onChangeForm={onChangeForm} />
                <SpecificFormService formState={formState} onChangeForm={onChangeForm} services={services} />
            </Form>
        </Card.FormStep>
    );
}

/**
 * ServiceCommissionCard is the card containing all informations about weezevent commission.
 * This card is visible only by admin staff.
 * @param formState - obj - form
 * @param onChangeForm - Func - On change one field in form
 * @param onChangeFormObject - Func - On change all form
 * @param organization - obj - Organization object
 */
function ServiceCommissionCard({ formState, onChangeForm, onChangeFormObject, organization, locale }) {
    const { t } = useTranslation();

    /**
     * Get the income for organizer according the weez fees
     */
    const organizer_income = React.useMemo(() => {
        let price = formState.price / 100;

        if (formState.weezevent_commission_type === 'fixed') {
            return price - formState.weezevent_commission / 100;
        }

        return price - (formState.weezevent_commission * 100 * price) / 100;
    }, [formState]);

    const onChangeComission = React.useCallback(
        ({ value }) => {
            const { weezevent_commission_type } = formState;
            onChangeForm('weezevent_commission', weezevent_commission_type === 'fixed' ? value * 100 : value / 100);
        },
        [formState, onChangeForm]
    );

    return (
        <Card.FormStep
            title={
                <div>
                    {t(`ticket.services.form.header.commissionTitleCard`)} <AdminStatus />
                </div>
            }
            subtitle={t(`ticket.services.form.header.commissionSubtitleCard`)}
            rightComponent={<></>}
        >
            <Form>
                <Form.Label>{t('ticket.services.form.content.weezeventCommissionType')}</Form.Label>
                <div>
                    <RadioLabel
                        name="commission_type"
                        value="fixed"
                        inline
                        bordered
                        checked={formState.weezevent_commission_type === 'fixed'}
                        label={t('ticket.services.form.content.commissionFixed')}
                        onClick={() => {
                            onChangeFormObject({ ...formState, weezevent_commission_type: 'fixed', weezevent_commission: 0 });
                        }}
                    />

                    <RadioLabel
                        name="commission_type"
                        value="percent"
                        inline
                        bordered
                        checked={formState.weezevent_commission_type === 'percent'}
                        label={t('ticket.services.form.content.commissionPercent')}
                        onClick={() => {
                            onChangeFormObject({ ...formState, weezevent_commission_type: 'percent', weezevent_commission: 0 });
                        }}
                    />
                </div>
                <CurrencyInputLabel
                    key={'commission'}
                    label={t('ticket.services.form.content.weezeventCommission')}
                    style={{ width: '122px' }}
                    placeholder={t('ticket.services.form.content.weezeventCommissionPlaceholder')}
                    currency={organization.currency}
                    locale={locale}
                    symbol={formState.weezevent_commission_type === 'percent' ? '%' : null}
                    max={formState.weezevent_commission_type === 'fixed' ? (formState.price > 0 ? formState.price / 100 + 1 : 0) : 101} // 101 because max is limit that we cannot reach
                    numberDecimals={2}
                    min={0}
                    value={formState.weezevent_commission_type === 'fixed' ? formState.weezevent_commission / 100 : formState.weezevent_commission * 100}
                    onChange={onChangeComission}
                />
                <Form.Label>{t('ticket.services.form.content.organizer_income', { amount: organizer_income.toFixed(1) })}</Form.Label>
            </Form>
        </Card.FormStep>
    );
}

export default function ServiceForm({ organization, serviceId }) {
    const { t } = useTranslation();
    const history = useHistory();
    const toast = React.useContext(ToastContext);
    const locale = i18n.language;

    const [unlimitedQuota, setUnlimitedQuota] = React.useState(false);

    let [formState, onChangeForm, onChangeFormObject, formErrors, formValidate] = useForm(
        {
            name: '',
            service: null,
            organization: organization.pk,
            price: 0,
            weezevent_commission: 0,
            weezevent_commission_type: 'fixed',
            description: '',
            expires_at: null,
            tax: null,
            quota: null,
            config: {}
        },
        {
            name: requiredField,
            service: requiredField
        }
    );

    // Get service config only if we have a serviceId
    const [, loading, ,] = ServiceConfigModel.useApiModel({
        organizationId: organization.id,
        id: serviceId,
        launch: Boolean(serviceId),
        onError: () => {
            toast.error(t('ticket.services.form.retrieveServiceError'));
        },
        onSuccess: service_tmp => {
            onChangeFormObject({
                uuid: service_tmp.uuid,
                name: service_tmp.name,
                service: service_tmp.service,
                organization: organization.pk,
                price: service_tmp.price,
                weezevent_commission: service_tmp.weezevent_commission,
                weezevent_commission_type: service_tmp.weezevent_commission_type,
                description: service_tmp.description,
                expires_at: service_tmp.expires_at,
                tax: service_tmp.tax,
                quota: service_tmp.quota,
                config: service_tmp.config
            });
            if (service_tmp.quota === null) {
                setUnlimitedQuota(true);
            }
        }
    });

    const [taxes, loadingTaxes] = TaxModel.useApiModel({});
    const [services, loadingServices] = ServiceModel.useApiModel({
        organizationId: organization.id
    });

    const servicesOptions = React.useMemo(() => {
        if (!services) {
            return [];
        }

        return services.map(service => ({
            key: service.uuid,
            text: `${service.name} - ${service.service_type}`,
            value: service.uuid
        }));
    }, [services]);

    // Validate form and save service
    const handleSubmit = React.useCallback(() => {
        if (!formValidate()) {
            return toast.error(t('ticket.services.form.alert.fieldError'));
        }
        let form = { ...formState };
        if (form.config.price_offset && form.config.price_offset.type === 'default') {
            delete form.config.price_offset;
        }
        // Transform prices in euros to cents
        let service = new ServiceConfigModel(form);
        service
            .save({ organizationId: organization.pk, pk: serviceId })
            .then(() => {
                toast.success(t('ticket.services.form.alert.saveSucess'));
                history.push(`/ticket/O${organization.id}/services`);
            })
            .catch(e => {
                toast.error(t('ticket.services.form.alert.saveError'));
            });
    }, [formState, serviceId, formValidate]);

    const onChangeType = React.useCallback(
        (field, value) => {
            let tmp = { service: value };
            let service = services.find(s => s.uuid === value);
            if (service && service.service_type === 'update_ticket_form') {
                let conf = { ...formState.config };
                conf.rotate_barcode = false;
                tmp.config = conf;
            }
            onChangeFormObject(tmp, true);
        },
        [formState, onChangeFormObject, services]
    );

    const onChangePrice = React.useCallback(
        ({ value }) => {
            let tmp = {
                price: value * 100,
                ...(formState.weezevent_commission_type === 'fixed' && value < formState.weezevent_commission && { weezevent_commission: 0 })
            };
            onChangeFormObject(tmp, true);
        },
        [formState, onChangeFormObject]
    );

    if (!formState || (serviceId && loading && loadingTaxes && loadingServices)) {
        // if serviceId, wait loading = false because description field will not load
        return <Loader />;
    }

    return (
        <>
            <WeezHeader
                title={serviceId && formState.name.length ? formState.name : t('ticket.services.form.header.newService')}
                backLink={{ onClick: () => history.push(`/ticket/O${organization.id}/services`) }}
                rightComponent={[<Button primary key={'submit'} onClick={handleSubmit} label={t(`ticket.common.action.save`)} />]}
            />
            <Card.FormStep
                title={t(`ticket.services.form.header.informationTitleCard`)}
                subtitle={t(`ticket.services.form.header.informationSubtitleCard`)}
                rightComponent={
                    <Message type="tips" title={t('ticket.common.tips')} mini>
                        <Trans i18nKey="ticket.services.infos.tips" />
                    </Message>
                }
            >
                <Form>
                    <div>
                        <Form.Field>
                            <SelectLabel
                                required={true}
                                placeholder={t('ticket.common.action.select')}
                                value={formState.service}
                                label={t('ticket.services.form.content.service')}
                                options={servicesOptions}
                                onChange={(synth, data) => onChangeType('service', data.value)}
                            />
                            <Form.FieldErrors errors={formErrors['service'] ? [t(formErrors['service'])] : ['']} />
                        </Form.Field>
                        <div className={Style['price-and-name-container']}>
                            <div className={Style['service-name-container']}>
                                <Form.Field error={formErrors['name'] ? true : false}>
                                    <Form.Label required={true}>{t('ticket.services.form.content.name')}</Form.Label>
                                    <Input
                                        required={true}
                                        value={formState.name}
                                        onChange={({ target: { value } }) => {
                                            onChangeForm('name', value);
                                        }}
                                        placeholder={t('ticket.services.form.content.namePlaceholder')}
                                    />
                                    <Form.FieldErrors errors={formErrors['name'] ? [t(formErrors['name'])] : ['']} />
                                </Form.Field>
                            </div>

                            <div className={Style['service-price-container']}>
                                <CurrencyInputLabel
                                    key={'price'}
                                    label={t('ticket.services.form.content.price')}
                                    placeholder={0}
                                    currency={organization.currency}
                                    locale={locale}
                                    numberDecimals={2}
                                    min={0}
                                    value={formState.price / 100}
                                    onChange={onChangePrice}
                                />
                            </div>
                        </div>

                        <Form.CounterLabel
                            style={{ width: '122px' }}
                            placeholder={1}
                            label={t('ticket.services.form.content.quota')}
                            onChange={(event, value) => onChangeForm('quota', value)}
                            value={formState.quota}
                            min={1}
                            disabled={unlimitedQuota}
                        />

                        <CheckboxLabel
                            onChange={(event, { checked }) => {
                                if (!checked) {
                                    onChangeForm('quota', 0);
                                } else {
                                    onChangeForm('quota', null);
                                }
                                setUnlimitedQuota(checked);
                            }}
                            key={'unlimited_quota'}
                            label={t('ticket.services.form.content.quotaToggle')}
                            checked={unlimitedQuota}
                        />

                        <SelectTaxes
                            label={t('ticket.services.form.content.tax')}
                            taxes={taxes}
                            onChange={(optionValue, option) => onChangeForm('tax', optionValue)}
                            value={formState.tax}
                        />

                        <Form.Label>{t('ticket.services.form.content.description')}</Form.Label>
                        <EditorComponent
                            description={formState.description}
                            onChange={(content, editor) => onChangeForm('description', content)}
                            placeholder={t('ticket.services.form.content.descriptionPlaceholder')}
                        />
                    </div>
                </Form>
            </Card.FormStep>
            <Auth allow={['RoleCapacity.ADMIN', 'PyvarCapacity.ADMIN']}>
                <ServiceCommissionCard formState={formState} onChangeForm={onChangeForm} onChangeFormObject={onChangeFormObject} organization={organization} locale={locale} />
            </Auth>
            <OptionalForm
                labelOpen={<span>{t(`ticket.services.form.optionalSettings.titleOpen`)}</span>}
                labelClosed={<span>{t(`ticket.services.form.optionalSettings.titleClosed`)}</span>}
                open={Boolean(formState.config)}
                displayAdditional
            >
                <ServiceAdvancedCard formState={formState} onChangeForm={onChangeForm} services={services} />
            </OptionalForm>
        </>
    );
}
